"use client";

import * as React from "react";
import { GripVertical, Plus } from "lucide-react";
import { cn } from "../../../lib/utils";
import { IconButton } from "../icon-button";
import { StatusDot } from "../status-dot";
import { kanbanColumnHeaderVariants } from "./constants";
import {
  useKanbanColumn,
  useKanbanBoard,
  useKanbanColumnDragHandle,
} from "./context";
import type { KanbanColumnHeaderProps } from "./types";

const KanbanColumnHeader = React.forwardRef<
  HTMLDivElement,
  KanbanColumnHeaderProps
>(({ className, ...props }, ref) => {
  const { column } = useKanbanColumn();
  const { onAddCard, onColumnMove } = useKanbanBoard();
  const { attributes, listeners } = useKanbanColumnDragHandle();
  const canDrag = !!onColumnMove;
  const atWipLimit =
    !!column.wipLimit &&
    column.wipLimit > 0 &&
    column.cards.length >= column.wipLimit;

  return (
    <div
      ref={ref}
      className={cn(
        kanbanColumnHeaderVariants({ draggable: canDrag }),
        className,
      )}
      {...props}
    >
      <div
        className="flex items-center gap-2 flex-1 min-w-0"
        {...(canDrag ? attributes : {})}
        {...(canDrag
          ? (listeners as React.HTMLAttributes<HTMLDivElement>)
          : {})}
      >
        {canDrag && (
          <GripVertical
            className="size-3.5 shrink-0 text-text-tertiary"
            aria-hidden="true"
          />
        )}

        <StatusDot size="sm" color={column.color} label={column.title} />

        <span className="truncate text-xs font-semibold text-text">
          {column.title}
        </span>

        {/* §5.20: the count is meta text, not a filled pill. A breached WIP
            limit is carried by colour alone. */}
        <span
          className={cn(
            "shrink-0 text-xs tabular-nums",
            atWipLimit ? "text-warning" : "text-text-tertiary",
          )}
        >
          {column.cards.length}
          {column.wipLimit && column.wipLimit > 0 ? ` / ${column.wipLimit}` : ""}
        </span>
      </div>

      {onAddCard && (
        <IconButton
          size="sm"
          label={`Add card to ${column.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onAddCard(column.id);
          }}
        >
          <Plus aria-hidden="true" />
        </IconButton>
      )}
    </div>
  );
});

KanbanColumnHeader.displayName = "KanbanColumnHeader";

export { KanbanColumnHeader };
